import { inject, Injectable } from "@angular/core";
import { AuthService } from "app/core/auth/auth.service";
import { ApiResponse } from "app/shared/models";
import { HttpClientService } from "app/shared/services/http.client.service";
import { BehaviorSubject, catchError, distinctUntilChanged, map, Observable, of, shareReplay, tap } from "rxjs";
import { parseClaims, PermissionClaims } from "./permission-claims";

@Injectable({ providedIn: "root" })
export class PermissionService {
  private _http = inject(HttpClientService);
  private _authService = inject(AuthService);
  private _codes = new BehaviorSubject<Set<string>>(new Set());
  private _load$: Observable<string[]> | null = null;
  private _loadedToken: string | null = null;

  /**
   * Carrega os codes do usuário logado uma vez por token. Chamado pelo
   * resolver inicial e pelo guard (que roda antes do resolver em deep links).
   * Em erro fica sem permissões e não guarda a falha.
   */
  load(): Observable<string[]> {
    const token = this._authService.accessToken || null;

    if (this._load$ && this._loadedToken === token) {
      return this._load$;
    }

    this._loadedToken = token;
    this._load$ = this._http.get<ApiResponse<string[]>>("/auth/me/permissoes").pipe(
      map((response) => response.data ?? []),
      catchError(() => {
        if (this._loadedToken === token) {
          this._load$ = null;
        }
        return of([] as string[]);
      }),
      tap((codes) => {
        if (this._loadedToken === token) {
          this._codes.next(new Set(codes));
        }
      }),
      shareReplay(1),
    );

    return this._load$;
  }

  has(code: string): boolean {
    return this._codes.value.has(code);
  }

  has$(code: string): Observable<boolean> {
    return this._codes.pipe(
      map((codes) => codes.has(code)),
      distinctUntilChanged(),
    );
  }

  get claims(): PermissionClaims {
    return parseClaims(this._authService.accessToken);
  }

  /** Admin e staff ignoram checagens de permissão no backend. */
  get isFullAccess(): boolean {
    const claims = this.claims;
    return claims.isAdmin || claims.isStaff;
  }
}
